import { PageShell } from '@/components/common/PageShell'
import { PermissionGate } from '@/components/common/PermissionGate'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuthStore } from '@/stores/useAuthStore'
import { DashboardCompaniesCard } from '@/features/companies/components/DashboardCompaniesCard'
import { ProbationReviewsCard } from '@/features/hr/components/probation/ProbationReviewsCard'
import { CasesNeedingYouCard } from '@/features/hr/components/cases/CasesNeedingYouCard'
import { LettersToSignCard } from '@/features/hr/components/letters/LettersToSignCard'
import { PoliciesToAcknowledgeCard } from '@/features/hr/components/my-policies/PoliciesToAcknowledgeCard'
import { StaffDashboard } from './StaffDashboard'
import { OrganizationDashboardOverview } from './OrganizationDashboardOverview'
import { LayoutDashboard } from 'lucide-react'

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

/**
 * Renders a permission-driven dashboard.
 * Users with companies:read see the companies overview card.
 * Non-admin users with attendance self-service access also see the session widget.
 */
export function DashboardPage(): React.ReactElement {
  const { can } = usePermissions()
  const canReadCompanies = can('companies', 'read')
  const user = useAuthStore((s) => s.user)
  const firstName = user?.firstName ?? user?.name?.split(' ')[0] ?? 'there'

  if (canReadCompanies) {
    return (
      <PermissionGate module="companies" action="read">
        <PageShell
          breadcrumbItems={[{ label: 'Dashboard' }]}
          icon={LayoutDashboard}
          title={`${getGreeting()}, ${firstName}`}
          description="Manage your organization, shifts, and attendance."
          className="gap-0"
          hideHeader
        >
          <div className="space-y-6">
            <OrganizationDashboardOverview
              greeting={`${getGreeting()}, ${firstName}`}
              subtitle="Manage your organization, shifts, and attendance."
            />
            <LettersToSignCard />
            <PoliciesToAcknowledgeCard />
            <ProbationReviewsCard />
            <CasesNeedingYouCard />
            <DashboardCompaniesCard />
            <StaffDashboard omitPageHeader />
          </div>
        </PageShell>
      </PermissionGate>
    )
  }

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Dashboard' }]}
      icon={LayoutDashboard}
      title={`${getGreeting()}, ${firstName}`}
      description="View your attendance, shifts, and leave requests."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-6">
        <DashboardGreeting
          greeting={`${getGreeting()}, ${firstName}`}
          subtitle="View your attendance, shifts, and leave requests."
        />
        <LettersToSignCard />
        <PoliciesToAcknowledgeCard />
        <ProbationReviewsCard />
        <CasesNeedingYouCard />
        <StaffDashboard omitPageHeader />
      </div>
    </PageShell>
  )
}

/** Compact welcome heading shown at the top of the dashboard body (no big icon tile). */
function DashboardGreeting({
  greeting,
  subtitle,
}: {
  greeting: string
  subtitle: string
}): React.ReactElement {
  return (
    <div className="pt-1">
      <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100 sm:text-2xl">
        {greeting}
      </h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
    </div>
  )
}
